import { useState } from 'react'
import { Box, Text, VStack, HStack, IconButton, Divider, Link } from '~/design_system'
import { ThumbsUp, SingleSparkle } from '~/assistant/icons'
import { MarkdownDisplay } from '~/components/v2/MarkdownDisplay'
import { Msg, useMsg } from '~/common/localizationUtils'
import type { AssistantSource } from '~/api/assistant'
import type { ThreadItem } from '../types'

export const QuestionResponse = ({ threadItem }: { threadItem: ThreadItem }) => (
  <VStack spacing={3} align="stretch">
    <Box bg="gray.200" p={3} borderRadius="8px" borderTopRightRadius="0" width="100%">
      <Text fontSize="16px" fontWeight="500">
        {threadItem.question}
      </Text>
    </Box>
    <AssistantResponse threadItem={threadItem} />
  </VStack>
)

const AssistantResponse = ({ threadItem }: { threadItem: ThreadItem }) => {
  if (threadItem.isPending) {
    return (
      <Box bg="white" border="1px solid" borderColor="gray.300" borderRadius="8px" width="100%" p={3}>
        <Box mb={2}>
          <SingleSparkle boxSize={6} color="black" />
        </Box>
        <Text fontSize="14px" color="gray.600">
          <Msg id="assistant.ai.message.thinking" />
        </Text>
      </Box>
    )
  }

  if (threadItem.error) {
    return (
      <Box bg="red.50" border="1px solid" borderColor="red.200" borderRadius="8px" width="100%" p={3}>
        <Text fontSize="14px" color="red.700">
          {threadItem.error}
        </Text>
      </Box>
    )
  }

  if (!threadItem.answer) return null
  return <AssistantAnswer answer={threadItem.answer} citations={threadItem.citations} />
}

const AssistantAnswer = ({ answer, citations }: { answer: string; citations?: AssistantSource[] }) => {
  const msg = useMsg()
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null)
  const toggleFeedback = (value: 'up' | 'down') => setFeedback(feedback === value ? null : value)

  return (
    <Box bg="white" border="1px solid" borderColor="gray.300" borderRadius="8px" width="100%">
      <Box p={3}>
        <Box mb={2}>
          <SingleSparkle boxSize={6} color="black" />
        </Box>
        <Box sx={{ '& > * + *': { mt: 2 } }}>
          <MarkdownDisplay>{answer}</MarkdownDisplay>
        </Box>
        {!!citations?.length && (
          <VStack spacing={1} align="stretch" mt={3}>
            <Text fontSize="xs" fontWeight="600" color="gray.500">
              <Msg id="assistant.ai.message.sources" />
            </Text>
            {citations.map(citation =>
              citation.url ? (
                <Link key={citation.url} href={citation.url} isExternal fontSize="xs">
                  {citation.title}
                </Link>
              ) : (
                <Text key={citation.title} fontSize="xs" color="gray.600">
                  {citation.title}
                </Text>
              )
            )}
          </VStack>
        )}
      </Box>
      <Divider />
      <HStack spacing={2} p={2} justify="flex-start">
        <IconButton
          aria-label={msg('assistant.ai.feedback.helpful')}
          icon={<ThumbsUp />}
          size="xs"
          variant="ghost"
          color={feedback === 'up' ? 'blue.500' : 'black'}
          bg={feedback === 'up' ? 'blue.100' : 'transparent'}
          borderRadius="full"
          onClick={() => toggleFeedback('up')}
          _hover={{ color: 'blue.500', bg: 'blue.50' }}
        />
        <IconButton
          aria-label={msg('assistant.ai.feedback.not_helpful')}
          icon={<ThumbsUp transform="rotate(180deg)" />}
          size="xs"
          variant="ghost"
          color={feedback === 'down' ? 'red.500' : 'black'}
          bg={feedback === 'down' ? 'red.100' : 'transparent'}
          borderRadius="full"
          onClick={() => toggleFeedback('down')}
          _hover={{ color: 'red.500', bg: 'red.50' }}
        />
      </HStack>
    </Box>
  )
}
